import React from 'react';
import { Instagram, Facebook, Linkedin, Twitter, ChevronLeft, ChevronRight, TrendingUp, Eye, Heart, MessageCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface PlannerContentProps {
  animationDelay?: number;
}

// Full month calendar data
const weeks = [
  [null, null, 1, 2, 3, 4, 5],
  [6, 7, 8, 9, 10, 11, 12],
  [13, 14, 15, 16, 17, 18, 19],
  [20, 21, 22, 23, 24, 25, 26],
  [27, 28, 29, 30, 31, null, null],
];

const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const scheduledPosts: Record<number, { platform: string; color: string }[]> = {
  3: [{ platform: 'instagram', color: '#E4405F' }],
  5: [{ platform: 'linkedin', color: '#0A66C2' }],
  8: [{ platform: 'facebook', color: '#1877F2' }, { platform: 'instagram', color: '#E4405F' }],
  10: [{ platform: 'twitter', color: '#1DA1F2' }],
  12: [{ platform: 'instagram', color: '#E4405F' }],
  15: [{ platform: 'linkedin', color: '#0A66C2' }, { platform: 'facebook', color: '#1877F2' }],
  17: [{ platform: 'instagram', color: '#E4405F' }],
  19: [{ platform: 'twitter', color: '#1DA1F2' }],
  22: [{ platform: 'instagram', color: '#E4405F' }, { platform: 'linkedin', color: '#0A66C2' }],
  24: [{ platform: 'facebook', color: '#1877F2' }],
  26: [{ platform: 'instagram', color: '#E4405F' }],
  29: [{ platform: 'linkedin', color: '#0A66C2' }],
  31: [{ platform: 'twitter', color: '#1DA1F2' }, { platform: 'instagram', color: '#E4405F' }],
};

const engagementStats = [
  { icon: Eye, label: 'Impressions', value: '124.5K', change: '+18.2%', color: '#3B82F6' },
  { icon: Heart, label: 'Engagement', value: '8,492', change: '+12.4%', color: '#EF4444' },
  { icon: MessageCircle, label: 'Comments', value: '1,247', change: '+24.1%', color: '#10B981' },
  { icon: TrendingUp, label: 'Reach', value: '89.2K', change: '+9.8%', color: '#8B5CF6' },
];

const PlannerContent: React.FC<PlannerContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const today = 15;

  return (
    <div 
      className="flex-1 p-1.5 lg:p-3 overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#222222' : '#F9F9F9',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between mb-2"
        style={{ animation: `slideInFromTop 0.3s ease-out ${animationDelay + 100}ms both` }}
      >
        <div className="flex items-center gap-1.5">
          <button 
            className="p-0.5 rounded hover:opacity-70"
            style={{ backgroundColor: isDark ? '#2A2A2A' : '#EBEBEB' }}
          >
            <ChevronLeft className="w-3 h-3" style={{ color: isDark ? '#999999' : '#666666' }} />
          </button>
          <span 
            className="text-xs font-semibold"
            style={{ color: isDark ? '#FFFFFF' : '#111111' }}
          >
            January 2025
          </span>
          <button 
            className="p-0.5 rounded hover:opacity-70"
            style={{ backgroundColor: isDark ? '#2A2A2A' : '#EBEBEB' }}
          >
            <ChevronRight className="w-3 h-3" style={{ color: isDark ? '#999999' : '#666666' }} />
          </button>
        </div>
        <div className="flex items-center gap-0.5">
          {[
            { Icon: Instagram, color: '#E4405F' },
            { Icon: Facebook, color: '#1877F2' },
            { Icon: Linkedin, color: '#0A66C2' },
            { Icon: Twitter, color: '#1DA1F2' },
          ].map(({ Icon, color }, i) => (
            <div 
              key={i}
              className="w-4 h-4 rounded flex items-center justify-center"
              style={{ backgroundColor: `${color}20` }}
            >
              <Icon className="w-2.5 h-2.5" style={{ color }} />
            </div>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      <div 
        className="rounded-lg overflow-hidden mb-2"
        style={{ 
          backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
          border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
          animation: `scaleIn 0.4s ease-out ${animationDelay + 200}ms both`
        }}
      >
        {/* Day headers */}
        <div 
          className="grid grid-cols-7 border-b"
          style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}
        >
          {days.map((day, index) => (
            <div 
              key={index}
              className="py-1 text-center text-[9px] font-medium"
              style={{ 
                color: isDark ? '#666666' : '#999999',
                animation: `fadeIn 0.3s ease-out ${animationDelay + 250 + (index * 20)}ms both`
              }}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar weeks */}
        {weeks.map((week, weekIndex) => (
          <div 
            key={weekIndex}
            className="grid grid-cols-7 border-b last:border-b-0"
            style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}
          >
            {week.map((date, dayIndex) => {
              const posts = date ? scheduledPosts[date] || [] : [];
              const isToday = date === today;
              
              return (
                <div 
                  key={dayIndex}
                  className="h-10 p-0.5 border-r last:border-r-0 relative"
                  style={{ 
                    borderColor: isDark ? '#333333' : '#E5E5E5',
                    backgroundColor: isToday 
                      ? (isDark ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.08)')
                      : 'transparent',
                    animation: date ? `fadeIn 0.2s ease-out ${animationDelay + 300 + (weekIndex * 30) + (dayIndex * 10)}ms both` : undefined
                  }}
                >
                  {date && (
                    <>
                      <div 
                        className="text-[9px] font-medium text-center"
                        style={{ 
                          color: isToday 
                            ? '#3B82F6'
                            : (isDark ? '#AAAAAA' : '#555555')
                        }}
                      >
                        {date}
                      </div>
                      
                      {/* Post indicators */}
                      {posts.length > 0 && (
                        <div className="flex justify-center gap-0.5 mt-0.5">
                          {posts.slice(0, 2).map((post, i) => (
                            <div
                              key={i}
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ 
                                backgroundColor: post.color,
                                animation: `scaleIn 0.2s ease-out ${animationDelay + 400 + (weekIndex * 50)}ms both`
                              }}
                            />
                          ))}
                          {posts.length > 2 && (
                            <span className="text-[6px]" style={{ color: isDark ? '#666666' : '#999999' }}>
                              +{posts.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Engagement Metrics */}
      <div 
        className="grid grid-cols-2 lg:grid-cols-4 gap-1 lg:gap-1.5"
        style={{ animation: `slideInFromBottom 0.4s ease-out ${animationDelay + 500}ms both` }}
      >
        {engagementStats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div
              key={index}
              className="p-1 lg:p-1.5 rounded-lg text-center"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
                border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
                animation: `scaleIn 0.3s ease-out ${animationDelay + 550 + (index * 50)}ms both`
              }}
            >
              <Icon className="w-2.5 h-2.5 lg:w-3 lg:h-3 mx-auto mb-0.5" style={{ color: stat.color }} />
              <div 
                className="text-[8px] lg:text-[10px] font-bold"
                style={{ color: isDark ? '#FFFFFF' : '#111111' }}
              >
                {stat.value}
              </div>
              <div 
                className="text-[6px] lg:text-[8px]"
                style={{ color: '#22C55E' }}
              >
                {stat.change}
              </div>
              <div 
                className="text-[6px] lg:text-[7px] hidden sm:block"
                style={{ color: isDark ? '#666666' : '#999999' }}
              >
                {stat.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Upcoming Posts Summary - hidden on mobile */}
      <div 
        className="hidden sm:flex mt-1.5 p-1.5 rounded-lg items-center justify-between"
        style={{ 
          backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
          border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
          animation: `fadeIn 0.4s ease-out ${animationDelay + 700}ms both`
        }}
      >
        <span className="text-[9px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
          Next: Instagram post in 2h 15m
        </span>
        <div 
          className="px-1.5 py-0.5 rounded text-[8px] font-medium"
          style={{ 
            backgroundColor: isDark ? '#2A2A2A' : '#F0F0F0',
            color: isDark ? '#888888' : '#666666'
          }}
        >
          18 posts
        </div>
      </div>
    </div>
  );
};

export default PlannerContent;
